import { createFileRoute } from "@tanstack/react-router";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { PageContainer, PageHeader } from "@/components/layout/page";
import { MonoText } from "@/components/display/mono";
import { LoadingState, ErrorState } from "@/components/feedback/states";
import { useTenantKey, usePkiConfig } from "@/api/settings";

export const Route = createFileRoute("/_app/settings/keys")({
  component: KeysSettings,
});

function KeysSettings() {
  const key = useTenantKey();
  const pki = usePkiConfig();

  if (key.isLoading) return <LoadingState />;
  if (key.error) return <ErrorState error={key.error} onRetry={() => key.refetch()} />;

  const k = key.data;
  const customKey = Boolean(k?.configured) || (k?.keyProvider ?? "SYSTEM") !== "SYSTEM";
  const p = pki.data ?? {};
  const pkiConfigured = Boolean(p.publicKeyPem ?? p.configured ?? p.algorithm);

  return (
    <PageContainer>
      <PageHeader
        title="Encryption Keys"
        description="Bring-your-own-key (CMEK) for data at rest and the asymmetric PKI key pair for regulated payloads."
      />
      <Box
        sx={{
          display: "grid",
          gap: 3,
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        }}
      >
        <Card sx={{ height: "100%" }}>
          <CardContent>
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                <Typography variant="subtitle1">Tenant data key</Typography>
                {customKey ? (
                  <Chip size="small" color="success" label="Customer-managed" />
                ) : (
                  <Chip size="small" label="Platform master key" />
                )}
              </Stack>
              <Row label="Provider" value={k?.keyProvider ?? "SYSTEM"} />
              {k?.keyArn ? <Row label="Key ARN" value={k.keyArn} mono /> : null}
              <Typography variant="body2" color="text.secondary">
                {k?.message ??
                  "Envelope encryption keys are managed here. Rotation and provider changes are done via the platform API or a support request."}
              </Typography>
            </Stack>
          </CardContent>
        </Card>
        <Card sx={{ height: "100%" }}>
          <CardContent>
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                <Typography variant="subtitle1">PKI key pair</Typography>
                {pkiConfigured ? (
                  <Chip size="small" color="success" label="Configured" />
                ) : (
                  <Chip size="small" label="Not configured" />
                )}
              </Stack>
              {typeof p.algorithm === "string" ? (
                <Row label="Algorithm" value={p.algorithm} />
              ) : null}
              {typeof p.keyId === "string" ? <Row label="Key ID" value={p.keyId} mono /> : null}
              <Typography variant="body2" color="text.secondary">
                Used to sign and encrypt regulated outbound payloads (e.g. C-DOT MNRL). Generate or
                rotate the pair from the platform API.
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </PageContainer>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <Stack direction="row" spacing={1} sx={{ justifyContent: "space-between", gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      {mono ? (
        <MonoText sx={{ wordBreak: "break-all", textAlign: "right" }}>{value}</MonoText>
      ) : (
        <Typography variant="body2">{value}</Typography>
      )}
    </Stack>
  );
}
